import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { supabase } from '@/lib/supabase';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Copy, Check, Trash2, Loader2, Plus, TicketCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export default function AdminCodes() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [city, setCity] = useState('');
  const [count, setCount] = useState(5);
  const [filter, setFilter] = useState('all'); // all | unused | used
  const [copiedId, setCopiedId] = useState(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (window.innerWidth < 768) {
      setTimeout(() => {
        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
      }, 100);
    }
  }, []);

  useEffect(() => {
    supabase.auth.getUser().then(async ({ data: { user: authUser } }) => {
        if (!authUser) { navigate('/map'); return; }
        const { data: profile } = await supabase.from('users').select('*').eq('id', authUser.id).single();
        if (!profile || profile.role !== 'admin') {
          navigate('/map');
        } else {
          setUser({ ...authUser, ...profile });
        }
      }).catch(() => {
        navigate('/map');
      });
  }, [navigate]);

  const { data: codes = [], isLoading } = useQuery({
    queryKey: ['adminAffiliationCodes'],
    queryFn: async () => {
      const { data: codes } = await supabase.from('affiliation_codes').select('*').order('created_at', { ascending: false });
      return codes || [];
    },
    enabled: !!user?.id,
    staleTime: 0
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const codes = [];
      for (let i = 0; i < count; i++) {
        const code = `${city.trim().substring(0,3).toUpperCase()}-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
        const { data, error } = await supabase.from('affiliation_codes').insert({ code, city: city.trim(), is_used: false }).select().single();
        if (!error && data) codes.push(data);
      }
      return { codes };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['adminAffiliationCodes'] });
      toast.success(`${data.codes.length} code${data.codes.length > 1 ? 's' : ''} créé${data.codes.length > 1 ? 's' : ''}`);
      setCity('');
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || err.message);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (codeId) => {
      await supabase.from('affiliation_codes').delete().eq('id', codeId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminAffiliationCodes'] });
      toast.success('Code supprimé');
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || err.message);
    }
  });

  const handleCopy = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedId(code);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleCopyAll = () => {
    const unusedCodes = filteredCodes.filter(c => !c.is_used).map(c => c.code);
    if (unusedCodes.length === 0) return;
    navigator.clipboard.writeText(unusedCodes.join('\n'));
    toast.success(`${unusedCodes.length} code${unusedCodes.length > 1 ? 's' : ''} copié${unusedCodes.length > 1 ? 's' : ''}`);
  };

  const filteredCodes = codes.filter(c => {
    if (filter === 'unused') return !c.is_used;
    if (filter === 'used') return c.is_used;
    return true;
  });

  // Stats
  const totalCodes = codes.length;
  const usedCodes = codes.filter(c => c.is_used).length;
  const unusedCodes = totalCodes - usedCodes;
  const cities = [...new Set(codes.map(c => c.city))];

  if (!user) return null;

  return (
    <div className="p-4 md:p-6">
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate('/admin')}
          className="text-[#B3B6C2] hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h2 className="text-lg font-black uppercase tracking-wide text-white">Codes d'invitation</h2>
          <p className="text-[#B3B6C2] text-xs mt-0.5">Créer et suivre les codes d'affiliation admin</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto space-y-6">

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="bg-[#13151D] border-l-4 border-[#6E2BFF] p-3">
            <p className="text-[10px] uppercase tracking-widest text-[#B3B6C2]">Total</p>
            <p className="text-2xl font-black text-[#6E2BFF]">{totalCodes}</p>
          </Card>
          <Card className="bg-[#13151D] border-l-4 border-green-500 p-3">
            <p className="text-[10px] uppercase tracking-widest text-[#B3B6C2]">Utilisés</p>
            <p className="text-2xl font-black text-green-500">{usedCodes}</p>
          </Card>
          <Card className="bg-[#13151D] border-l-4 border-amber-500 p-3">
            <p className="text-[10px] uppercase tracking-widest text-[#B3B6C2]">Disponibles</p>
            <p className="text-2xl font-black text-amber-500">{unusedCodes}</p>
          </Card>
        </div>

        {/* Create form */}
        <Card className="p-4 bg-[#13151D] border border-[#2A2D3A]">
          <h3 className="text-sm font-bold text-white mb-3">Générer des codes</h3>
          <div className="flex gap-2">
            <Input
              placeholder="Ville (ex: Paris)"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="bg-[#1B1E28] border-[#2A2D3A] text-white flex-1"
            />
            <Input
              type="number"
              min={1}
              max={50}
              value={count}
              onChange={(e) => setCount(Math.min(50, Math.max(1, parseInt(e.target.value) || 1)))}
              className="bg-[#1B1E28] border-[#2A2D3A] text-white w-20 text-center"
            />
            <Button
              onClick={() => createMutation.mutate()}
              disabled={!city.trim() || createMutation.isPending}
              className="bg-[#6E2BFF] hover:bg-[#5a23d6] text-white font-bold"
            >
              {createMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            </Button>
          </div>
        </Card>

        {/* Filter + bulk copy */}
        <div className="flex items-center justify-between">
          <div className="flex gap-1">
            {['all', 'unused', 'used'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  filter === f
                    ? 'bg-[#6E2BFF] text-white'
                    : 'bg-[#1B1E28] text-[#B3B6C2] hover:text-white'
                }`}
              >
                {f === 'all' ? 'Tous' : f === 'unused' ? 'Disponibles' : 'Utilisés'}
              </button>
            ))}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopyAll}
            className="text-[#B3B6C2] hover:text-white text-xs"
          >
            <Copy className="w-3 h-3 mr-1" /> Copier dispo
          </Button>
        </div>

        {/* Codes list */}
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 text-[#6E2BFF] animate-spin" />
          </div>
        ) : filteredCodes.length === 0 ? (
          <Card className="p-6 text-center bg-[#13151D]">
            <TicketCheck className="w-8 h-8 text-[#6E2BFF]/30 mx-auto mb-2" />
            <p className="text-[#9CA0AB] text-sm">Aucun code</p>
          </Card>
        ) : (
          <div className="space-y-2">
            {filteredCodes.map((c) => (
              <Card
                key={c.id}
                className={`p-3 bg-[#13151D] border ${c.is_used ? 'border-green-500/20' : 'border-[#2A2D3A]'}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <code className={`text-sm font-mono font-bold ${c.is_used ? 'text-green-400' : 'text-white'}`}>
                        {c.code}
                      </code>
                      <span className="text-[10px] text-[#B3B6C2] bg-[#1B1E28] px-2 py-0.5 rounded">
                        {c.city}
                      </span>
                    </div>
                    {c.is_used ? (
                      <p className="text-xs text-green-400/70 mt-1">
                        Utilisé par @{c.used_by_username || '?'} le{' '}
                        {c.used_at ? new Date(c.used_at).toLocaleDateString('fr-FR') : '?'}
                      </p>
                    ) : (
                      <p className="text-xs text-[#9CA0AB] mt-1">
                        Créé le {new Date(c.created_at).toLocaleDateString('fr-FR')}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {!c.is_used && (
                      <>
                        <button
                          onClick={() => handleCopy(c.code)}
                          className="text-[#B3B6C2] hover:text-white p-1.5 transition-colors"
                        >
                          {copiedId === c.code ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => deleteMutation.mutate(c.id)}
                          disabled={deleteMutation.isPending}
                          className="text-red-400/50 hover:text-red-400 p-1.5 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Cities breakdown */}
        {cities.length > 0 && (
          <Card className="p-4 bg-[#13151D] border border-[#2A2D3A]">
            <h3 className="text-xs font-bold text-[#B3B6C2] uppercase tracking-widest mb-3">Par ville</h3>
            <div className="space-y-2">
              {cities.map(cityName => {
                const cityCodes = codes.filter(c => c.city === cityName);
                const cityUsed = cityCodes.filter(c => c.is_used).length;
                return (
                  <div key={cityName} className="flex items-center justify-between">
                    <span className="text-sm text-white font-medium">{cityName}</span>
                    <span className="text-xs text-[#B3B6C2]">
                      {cityUsed}/{cityCodes.length} utilisés
                    </span>
                  </div>
                );
              })}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
